import React, { useState } from 'react';

const EnhancedFeedbackWidget = ({ item, onSubmit, onCancel }) => {
  const [feedbackType, setFeedbackType] = useState(null);
  const [comment, setComment] = useState('');
  const [confidence, setConfidence] = useState(0.75);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!feedbackType) {
      alert('Veuillez sélectionner un type de feedback');
      return;
    }

    // Champ commentaire obligatoire pour "partial" et "no_good_match"
    if ((feedbackType === 'partial' || feedbackType === 'no_good_match') && !comment.trim()) {
      alert('Veuillez fournir un commentaire pour justifier votre choix');
      return;
    }

    setIsSubmitting(true);

    try {
      const feedbackData = {
        item_id: item.item_id || `item_${Date.now()}`,
        feedback_type: feedbackType,
        equipment_text: item.label || '',
        match_ref: item.match_ref || '',
        comment: comment.trim(),
        timestamp: new Date().toISOString(),
        extracted_specs: item.specs || {},
        validation_confidence: confidence,
        user_input: {
          selected_feedback_type: feedbackType,
          comment_length: comment.length
        }
      };

      await fetch('https://api.aimycore.fr/api/analyze/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackData)
      });

      if (onSubmit) {
        onSubmit(feedbackData);
      } else {
        alert(`✅ Feedback "${feedbackType}" enregistré !`);
      }

      // Reset form
      setFeedbackType(null);
      setComment('');
      setConfidence(0.75);
    } catch (error) {
      alert(`❌ Erreur: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3>Validation Équipement</h3>
        <button onClick={onCancel} style={styles.closeBtn}>✕</button>
      </div>

      {/* Équipement extrait */}
      <div style={styles.section}>
        <h4>📄 Équipement extrait</h4>
        <p style={styles.equipmentText}>{item.label || 'N/A'}</p>
      </div>

      {/* Specs détectées */}
      {item.specs && Object.keys(item.specs).length > 0 && (
        <div style={styles.section}>
          <h4>⚙️ Spécifications détectées</h4>
          <div style={styles.specsGrid}>
            {Object.entries(item.specs).map(([key, value]) => {
              if (value === null || value === undefined || key === 'category' || key === 'specs_incomplete') {
                return null;
              }
              return (
                <div key={key} style={styles.specItem}>
                  <span style={styles.specLabel}>{formatSpecLabel(key)}:</span>
                  <span style={styles.specValue}>{formatSpecValue(value)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Match trouvé */}
      {item.match_label && (
        <div style={styles.section}>
          <h4>✅ Match proposé</h4>
          <p style={styles.matchLabel}>{item.match_label}</p>
          {item.match_score && (
            <p style={styles.matchScore}>
              Score: {(item.match_score * 100).toFixed(0)}% confiance
            </p>
          )}
        </div>
      )}

      {/* Alternatives */}
      {item.alternatives && item.alternatives.length > 0 && (
        <div style={styles.section}>
          <h4>🔄 Alternatives trouvées</h4>
          <div style={styles.alternativesList}>
            {item.alternatives.slice(0, 3).map((alt, idx) => (
              <div key={idx} style={styles.alternativeItem}>
                <div style={styles.altHeader}>
                  <span>{alt.ref}</span>
                  <span style={styles.altScore}>{(alt.score * 100).toFixed(0)}%</span>
                </div>
                <p style={styles.altLabel}>{alt.label}</p>
                {alt.price && (
                  <p style={styles.altPrice}>€{alt.price}/jour</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Choix feedback */}
      <div style={styles.section}>
        <h4>📋 Validation</h4>
        <div style={styles.feedbackOptions}>
          <FeedbackButton
            label="✅ OK"
            subtext="Match exact"
            selected={feedbackType === 'correct'}
            onClick={() => setFeedbackType('correct')}
            color="#059669"
          />
          <FeedbackButton
            label="❌ Non"
            subtext="Pas valide"
            selected={feedbackType === 'incorrect'}
            onClick={() => setFeedbackType('incorrect')}
            color="#dc2626"
          />
          <FeedbackButton
            label="🔶 Partiel"
            subtext="Incomplet"
            selected={feedbackType === 'partial'}
            onClick={() => setFeedbackType('partial')}
            color="#f59e0b"
          />
          <FeedbackButton
            label="❓ Aucun"
            subtext="No match"
            selected={feedbackType === 'no_good_match'}
            onClick={() => setFeedbackType('no_good_match')}
            color="#6366f1"
          />
        </div>
      </div>

      {/* Commentaire (visible selon feedback type) */}
      {(feedbackType === 'incorrect' || feedbackType === 'partial' || feedbackType === 'no_good_match') && (
        <div style={styles.section}>
          <h4>💬 Commentaire/Justification</h4>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={getCommentPlaceholder(feedbackType)}
            style={styles.textarea}
          />
          <p style={styles.helperText}>
            {feedbackType === 'incorrect' && 'Ex: "Projecteur équivalent mais angle trop large"'}
            {feedbackType === 'partial' && 'Ex: "Match correct mais manque émetteur ULX-D"'}
            {feedbackType === 'no_good_match' && 'Ex: "Aucun micro HF UHF dans le catalogue"'}
          </p>
        </div>
      )}

      {/* Confiance */}
      {feedbackType && (
        <div style={styles.section}>
          <h4>🎯 Confiance de votre validation: {(confidence * 100).toFixed(0)}%</h4>
          <input
            type="range"
            min="0"
            max="100"
            value={confidence * 100}
            onChange={(e) => setConfidence(e.target.value / 100)}
            style={styles.slider}
          />
        </div>
      )}

      {/* Boutons action */}
      <div style={styles.actions}>
        <button onClick={onCancel} style={styles.cancelBtn}>
          Annuler
        </button>
        <button
          onClick={handleSubmit}
          disabled={!feedbackType || isSubmitting}
          style={{
            ...styles.submitBtn,
            opacity: !feedbackType || isSubmitting ? 0.5 : 1,
            cursor: !feedbackType || isSubmitting ? 'not-allowed' : 'pointer'
          }}
        >
          {isSubmitting ? 'Envoi...' : 'Soumettre Feedback'}
        </button>
      </div>
    </div>
  );
};

const FeedbackButton = ({ label, subtext, selected, onClick, color }) => (
  <button
    onClick={onClick}
    style={{
      ...styles.feedbackBtn,
      borderColor: selected ? color : '#e5e7eb',
      backgroundColor: selected ? `${color}15` : '#ffffff',
      borderWidth: selected ? '2px' : '1px'
    }}
  >
    <div style={{ fontSize: '18px' }}>{label}</div>
    <div style={{ fontSize: '12px', color: '#666' }}>{subtext}</div>
  </button>
);

const formatSpecLabel = (key) => {
  const labels = {
    brand: 'Marque',
    model: 'Modèle',
    type: 'Type',
    power_w: 'Puissance',
    coverage_h: 'Angle H',
    coverage_v: 'Angle V',
    frequency_hz_min: 'Fréq min',
    frequency_hz_max: 'Fréq max',
    connection: 'Connectique',
    sensitivity_db: 'Sensibilité',
    impedance_ohm: 'Impédance',
    beam_type: 'Beam type',
    color_temp_k: 'Couleur',
    resolution: 'Résolution',
    size_inch: 'Taille',
    technology: 'Technologie'
  };
  return labels[key] || key;
};

const formatSpecValue = (value) => {
  if (typeof value === 'number') {
    return value;
  }
  return String(value);
};

const getCommentPlaceholder = (feedbackType) => {
  switch (feedbackType) {
    case 'incorrect':
      return 'Expliquez pourquoi ce match n\'est pas valide...';
    case 'partial':
      return 'Décrivez ce qui manque ou est incomplet...';
    case 'no_good_match':
      return 'Précisez quel équipement serait nécessaire...';
    default:
      return 'Commentaire optionnel...';
  }
};

const styles = {
  container: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '20px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '10px'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#666'
  },
  section: {
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '1px solid #f0f0f0'
  },
  equipmentText: {
    fontSize: '14px',
    fontStyle: 'italic',
    color: '#555',
    padding: '10px',
    backgroundColor: '#f9fafb',
    borderRadius: '4px'
  },
  specsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
    marginTop: '10px'
  },
  specItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px',
    backgroundColor: '#f3f4f6',
    borderRadius: '4px',
    fontSize: '13px'
  },
  specLabel: {
    fontWeight: '600',
    color: '#374151'
  },
  specValue: {
    color: '#666',
    marginTop: '2px'
  },
  matchLabel: {
    fontSize: '14px',
    padding: '8px',
    backgroundColor: '#ecfdf5',
    borderRadius: '4px',
    color: '#065f46'
  },
  matchScore: {
    fontSize: '12px',
    color: '#059669',
    marginTop: '5px'
  },
  alternativesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '10px'
  },
  alternativeItem: {
    padding: '10px',
    backgroundColor: '#f0f9ff',
    borderRadius: '4px',
    borderLeft: '3px solid #0284c7'
  },
  altHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#0284c7'
  },
  altScore: {
    backgroundColor: '#dbeafe',
    padding: '2px 8px',
    borderRadius: '3px'
  },
  altLabel: {
    fontSize: '13px',
    color: '#064e3b',
    marginTop: '5px',
    margin: '5px 0 0 0'
  },
  altPrice: {
    fontSize: '11px',
    color: '#059669',
    marginTop: '3px',
    margin: '3px 0 0 0'
  },
  feedbackOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '10px',
    marginTop: '10px'
  },
  feedbackBtn: {
    padding: '10px',
    border: '1px solid',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'center',
    justifyContent: 'center'
  },
  textarea: {
    width: '100%',
    minHeight: '80px',
    padding: '10px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontFamily: 'inherit',
    fontSize: '13px',
    resize: 'vertical'
  },
  helperText: {
    fontSize: '12px',
    color: '#999',
    marginTop: '5px',
    fontStyle: 'italic'
  },
  slider: {
    width: '100%',
    marginTop: '10px'
  },
  actions: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px',
    justifyContent: 'flex-end'
  },
  cancelBtn: {
    padding: '10px 20px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    fontSize: '14px'
  },
  submitBtn: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: '#0284c7',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  }
};

export default EnhancedFeedbackWidget;
